import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useListBrandTemplates,
  useListUserProjects,
} from "@dataconnect/generated/react";
import { auth } from "@/src/config/firebase";
import { BrandIdentity } from "@/src/types.ts";
import { deserializeBrandTemplate } from "@/src/utils/brandTemplateHelpers.ts";
import BrandTemplatesManager from "@/src/components/BrandTemplatesManager.tsx";

const BrandSettings: React.FC = () => {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const {
    data: brandsData,
    refetch: refetchBrands,
    isLoading: isLoadingBrands,
  } = useListBrandTemplates({ enabled: !!user });

  const { data: projectsData } = useListUserProjects({ enabled: !!user });

  const globalBrands: BrandIdentity[] = React.useMemo(() => {
    if (!brandsData?.brandTemplates) return [];
    return brandsData.brandTemplates.map((template) =>
      deserializeBrandTemplate(template as any)
    );
  }, [brandsData]);

  const brandUsage = React.useMemo(() => {
    const counts: Record<string, number> = {};
    if (!projectsData?.user?.projects) return counts;
    projectsData.user.projects.forEach((project) => {
      const brandId = project.brandTemplate?.id;
      if (!brandId) return;
      counts[brandId] = (counts[brandId] || 0) + 1;
    });
    return counts;
  }, [projectsData]);

  return (
    <BrandTemplatesManager
      globalBrands={globalBrands}
      isLoadingBrands={isLoadingBrands}
      refetchBrands={refetchBrands}
      onAddBrand={() =>
        navigate({ to: "/onboarding", search: { returnTab: "library" } })
      }
      onUseTemplate={(brandId) => {
        navigate({
          to: "/",
          search: { returnTab: undefined, createWithBrandId: brandId },
        });
      }}
      brandUsage={brandUsage}
    />
  );
};

export const Route = createFileRoute("/settings/brand")({
  component: BrandSettings,
});
